import { CommonModule } from "@angular/common";
import { Component } from '@angular/core';
import { FormsModule, Validators } from "@angular/forms";

// EZ
import { InputConfig, Primitive } from "@template-ui/types";
import { DocMeta } from "@template-ui/decorators";
import { AppBaseComponent } from "../../../components/app-base.component";
import { EzFormComponent } from "@template-ui/components";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { SHOWCASE_LIBRARY_NAMES } from "../../showcase-library.constants";

import { DropdownKeys } from "../../../dropdown-configs/common.dropdown-config";

@DocMeta({
  hide: true,
  showcaseAttachTo: [
    { libName: SHOWCASE_LIBRARY_NAMES.BASE, classNames: ["EzFormComponent", "HighlightChangeDirective"] },
  ],
  showcaseTitle: "Basic Sample",
  showcaseDesc: `
  ## Dynamic Form Sample

  This sample demonstrates the usage of \`ui-form\` for building dynamic forms in Angular.

  - The form fields are defined using the \`InputConfig\` interface.
  - Each field's type, label, validation, options, and layout are specified in the configuration array.
  - The form automatically generates inputs, applies validation, and manages layout based on the config.

  ### About \`InputConfig\`

  The \`InputConfig\` class is the core structure for describing each input field in the dynamic form.
  Key properties include:

  - \`type\`: The type of input (e.g., 'text', 'number', 'select', 'date').
  - \`name\`: Unique identifier for the field.
  - \`label\`: Display label for the field.
  - \`validation\`: Array of Angular validator functions.
  - \`options\`: For dropdowns/radios, an array of selectable options.
  - \`dropdownKey\`/\`dropdownConfig\`: For dynamic dropdowns, references a config or key.
  - \`width\`, \`row\`: Controls layout and grouping.
  - Many other properties for advanced customization.

  <details>
  <!-- NOTE: To style the summary tag, you must use HTML instead of MD. Original MD:
    ### Using \`dropdownKey\` for Dynamic Dropdowns
  -->
  <summary><h3 class="inline m-0">Using <code>dropdownKey</code> for Dynamic Dropdowns</h3></summary>

  Several fields in this sample use the \`dropdownKey\` property to reference a registered dropdown configuration from \`DropdownKeys\`.
  This enables dynamic population of dropdown options from APIs or static lists.

  #### DropdownKeys Used in This Sample

  - \`DropdownKeys.institutionsNoBph\`: Populates the "Current Institution" dropdown with institution names, excluding BPH Headquarters.
  - \`DropdownKeys.sentenceTypes_IslDsl\`: Populates the "Sentence Type" dropdown with only ISL and DSL sentence types.
  - \`DropdownKeys.yesNo\`: Populates the "Elderly Parole?" dropdown with a simple Yes/No choice.

  #### Purpose & Usage of DropdownKeys

  - \`dropdownKey\` connects your form field to a reusable dropdown configuration, ensuring consistency and central management of dropdown logic.
  - You can override dropdown behavior or options by using \`dropdownOverride\` if needed.

  ### Using \`formValues\` to Populate Data

  The \`formValues\` object allows you to pre-populate the form with data, such as from an API response.

  - Pass your data object to the \`[formValues]\` input of the form component.
  - The form will automatically fill in each field with the corresponding value from the object, matching by field \`name\`.
  - This is useful for editing existing records or displaying details from a backend.

  </details>
  `
})
@Component({
  selector: 'app-dynamic-form-sample',
  imports: [
    // Angular
    CommonModule, FormsModule,

    // EZ
    UiLibraryModule
  ],
  templateUrl: './dynamic-form-sample.component.html',
  styleUrls: ['./dynamic-form-sample.component.scss']
})
export class DynamicFormSampleComponent extends AppBaseComponent {

  isDisabled: boolean = false;

  lastName: string = "Jones";

  delta: any;

  formValues: { [key: string]: Primitive } = {
    firstName: 'John',
    lastName: this.lastName, // <-- This is NOT automatically bound to this.lastName. If you want to do that, set up a getter/setter
    cdcNumber: 'ZZ1234',
    instCd: 'CCC',
    dateOfBirth: new Date(1990, 1, 1),
    currentAge: 30,
    ageAtCrime: 25,
    multipleFelonyCase: 'SINGLE',
    sentenceType: 'ISL',
    elderlyParole: 'NO',
    eligibleReleaseDate: new Date(2025, 1, 1),
    totalTimeInCustody: '5 years',
    iceHoldOrigin: 'USA',
    id: 'abc123'
  };

  get formValuesWithoutMetadata() {
    return EzFormComponent.stripProxyMetadata(this.formValues);
  }

  applicantInfoInputsConfig: InputConfig[] = [
    { type: "text", name: "firstName", label: "First Name", width: "1x", validation: [Validators.required], validationMessages: { required: 'First Name is required' } },
    { type: "text", name: "lastName", field: "lastName",label: "Last Name", width: "1x", validation: [Validators.required] },
    { type: "text", name: "lastName_1", field: "lastName",label: "Last Name", width: "1x", validation: [Validators.required] },
    { type: "text", name: "cdcNumber", label: "CDCR#", width: "1x", validation: [Validators.required] },
    { type: "select", name: "instCd", label: "Current Institution", width: "2x", dropdownKey: DropdownKeys.institutionsNoBph },
    //{ type: "select", name: "instCd", label: "Current Institution", width: "2x", optionsEndpoint: this.apis.core.getFiltersGetInstitutions() },

    { type: "date", name: "dateOfBirth", label: "Date Of Birth", width: "1x" },
    { type: "number", name: "currentAge", label: "Current Age", width: "1x" },
    { type: "number", name: "ageAtCrime", label: "Age at time of crime", width: "1x" },
    {
      type: "select", name: "multipleFelonyCase_sample", label: "Single or Multiple Felony?", width: "1x",
      options: [
        { value: "SINGLE", viewValue: "Single" },
        { value: "MULTIPLE", viewValue: "Multiple" },
        { value: "MISDEMEANOR", viewValue: "Misdemeanor" }
      ]
    },
    { type: "select", name: "sentenceType_sample", label: "Sentence Type", width: "1x", dropdownKey: DropdownKeys.sentenceTypes_IslDsl },
    { type: "select", name: "elderlyParole_sample", label: "Elderly Parole?", width: "1x", dropdownKey: DropdownKeys.yesNo },
    { type: "date", name: "eligibleReleaseDate_sample", label: "Eligible for release date", width: "1x" },
    { type: "text", name: "totalTimeInCustody_sample", label: "Total time in custody", width: "1x" },
    { type: "text", name: "iceHoldOrigin_sample", label: "ICE HOLD Country", width: "1x" },
    { type: "hidden", name: "id_sample", readOnly: true }
  ];
}
